import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import DogEditModal from './DogEditModal';
import './FoundReports.css';

function FoundReports({ currentUser }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDog, setFilterDog] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterReporter, setFilterReporter] = useState('');
  const [filterDetails, setFilterDetails] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending'); // default preserved
  const [actioningId, setActioningId] = useState(null);
  const [selectedDogName, setSelectedDogName] = useState(null);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc'); // newest first by default

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/found-reports');
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching found reports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleAction = async (reportId, status, dogAction) => {
    try {
      setActioningId(reportId);
      await api.put(`/api/found-reports/${reportId}`, {
        status,
        dog_action: dogAction || null
      });
      await fetchReports();
    } catch (error) {
      console.error('Error actioning report:', error);
      alert('Failed to update report. Please try again.');
    } finally {
      setActioningId(null);
    }
  };

  const handleSetStatus = async (report, newDogStatus, selectElement) => {
    if (!newDogStatus) return;
    const ok = window.confirm(
      `Set status of "${report.dog_name}" to "${newDogStatus}"? This will also mark the report as actioned.`
    );
    if (!ok) {
      // Reset the dropdown back to the placeholder.
      if (selectElement) selectElement.value = '';
      return;
    }
    try {
      setActioningId(report.id);
      await api.put(`/api/found-reports/${report.id}`, {
        status: 'actioned',
        dog_action: 'set_status',
        new_dog_status: newDogStatus,
      });
      await fetchReports();
    } catch (error) {
      console.error('Error setting dog status:', error);
      alert(error.response?.data?.error || 'Failed to set dog status. Please try again.');
      if (selectElement) selectElement.value = '';
    } finally {
      setActioningId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  const reportTypeLabel = (type) => {
    switch(type) {
      case 'adopted': return 'Adopted / Rehomed';
      case 'raced_again': return 'Raced Again';
      case 'other': return 'Other';
      default: return type;
    }
  };

  const handleHeaderClick = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortArrow = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const isAnyFilterActive = (
    filterDog.trim() !== '' ||
    filterType !== '' ||
    filterReporter.trim() !== '' ||
    filterDetails.trim() !== '' ||
    filterDateFrom !== '' ||
    filterDateTo !== '' ||
    filterStatus !== 'pending'
  );

  const displayedReports = useMemo(() => {
    const dogQ = filterDog.trim().toLowerCase();
    const reporterQ = filterReporter.trim().toLowerCase();
    const detailsQ = filterDetails.trim().toLowerCase();
    const fromTs = filterDateFrom ? new Date(filterDateFrom + 'T00:00:00').getTime() : null;
    const toTs = filterDateTo ? new Date(filterDateTo + 'T23:59:59').getTime() : null;

    const filtered = reports.filter((r) => {
      if (dogQ && !(r.dog_name || '').toLowerCase().includes(dogQ)) return false;
      if (filterType && r.report_type !== filterType) return false;
      if (reporterQ) {
        const reporter = `${r.reporter_name || ''} ${r.reporter_contact || ''}`.toLowerCase();
        if (!reporter.includes(reporterQ)) return false;
      }
      if (detailsQ && !(r.details || '').toLowerCase().includes(detailsQ)) return false;
      if (fromTs != null || toTs != null) {
        const ts = r.created_at ? new Date(r.created_at).getTime() : null;
        if (ts == null) return false;
        if (fromTs != null && ts < fromTs) return false;
        if (toTs != null && ts > toTs) return false;
      }
      if (filterStatus && r.status !== filterStatus) return false;
      return true;
    });

    const dir = sortDirection === 'asc' ? 1 : -1;
    const cmp = (a, b) => {
      if (sortColumn === 'created_at') {
        const at = a == null ? 0 : (new Date(a).getTime() || 0);
        const bt = b == null ? 0 : (new Date(b).getTime() || 0);
        return (at - bt) * dir;
      }
      const av = a == null ? '' : a;
      const bv = b == null ? '' : b;
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    };
    filtered.sort((a, b) => cmp(a[sortColumn], b[sortColumn]));
    return filtered;
  }, [reports, sortColumn, sortDirection, filterDog, filterType, filterReporter, filterDetails, filterDateFrom, filterDateTo, filterStatus]);

  const sortableHeaderProps = (column) => ({
    className: 'sortable',
    tabIndex: 0,
    'aria-sort': sortColumn === column ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none',
    onClick: () => handleHeaderClick(column),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleHeaderClick(column);
      }
    },
  });

  return (
    <div className="found-reports">
      <div className="reports-header">
        <div>
          <h2>Found Dog Reports</h2>
          <p>Review reports from the public about dogs found on the missing wall</p>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading reports...</div>
      ) : reports.length === 0 ? (
        <div className="no-reports">No reports found.</div>
      ) : (
        <div className="reports-table-wrapper">
          <table className="reports-table">
            <thead>
              <tr>
                <th {...sortableHeaderProps('dog_name')}>Dog{sortArrow('dog_name')}</th>
                <th {...sortableHeaderProps('report_type')}>Type{sortArrow('report_type')}</th>
                <th {...sortableHeaderProps('reporter_name')}>Reporter{sortArrow('reporter_name')}</th>
                <th>Details</th>
                <th {...sortableHeaderProps('created_at')}>Submitted{sortArrow('created_at')}</th>
                <th {...sortableHeaderProps('status')}>Status{sortArrow('status')}</th>
                <th>Actions</th>
              </tr>
              <tr className="filter-row">
                <th>
                  <input type="text" className="filter-input" placeholder="Filter…" value={filterDog} onChange={(e) => setFilterDog(e.target.value)} />
                </th>
                <th>
                  <select className="filter-input" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                    <option value="">All</option>
                    <option value="adopted">Adopted / Rehomed</option>
                    <option value="raced_again">Raced Again</option>
                    <option value="other">Other</option>
                  </select>
                </th>
                <th>
                  <input type="text" className="filter-input" placeholder="Filter…" value={filterReporter} onChange={(e) => setFilterReporter(e.target.value)} />
                </th>
                <th>
                  <input type="text" className="filter-input" placeholder="Filter…" value={filterDetails} onChange={(e) => setFilterDetails(e.target.value)} />
                </th>
                <th>
                  <div className="filter-date-row">
                    <input type="date" className="filter-input filter-date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} title="From" />
                    <input type="date" className="filter-input filter-date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} title="To" />
                  </div>
                </th>
                <th>
                  <select className="filter-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">All</option>
                    <option value="pending">Pending</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="actioned">Actioned</option>
                    <option value="dismissed">Dismissed</option>
                  </select>
                </th>
                <th>
                  {isAnyFilterActive && (
                    <button
                      type="button"
                      className="filter-clear-link"
                      onClick={() => {
                        setFilterDog(''); setFilterType(''); setFilterReporter('');
                        setFilterDetails(''); setFilterDateFrom(''); setFilterDateTo('');
                        setFilterStatus('pending');
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedReports.length === 0 ? (
                <tr>
                  <td colSpan={7} className="no-match-row">
                    No reports match the current filters.
                  </td>
                </tr>
              ) : displayedReports.map(report => (
                <tr key={report.id} className={`report-row status-${report.status}`}>
                  <td
                    className="dog-name-cell dog-name-cell-clickable"
                    title="Open dog record"
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedDogName(report.dog_name)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedDogName(report.dog_name);
                      }
                    }}
                  >
                    {report.dog_name}
                  </td>
                  <td>{reportTypeLabel(report.report_type)}</td>
                  <td>
                    {report.reporter_name || 'Anonymous'}
                    {report.reporter_contact && (
                      <div className="reporter-contact">{report.reporter_contact}</div>
                    )}
                  </td>
                  <td className="details-cell">{report.details || '—'}</td>
                  <td>{formatDate(report.created_at)}</td>
                  <td>
                    <span className={`status-badge status-${report.status}`}>{report.status}</span>
                  </td>
                  <td className="actions-cell">
                    {report.status === 'pending' && (
                      <div className="action-buttons">
                        <button
                          className="action-btn action-remove"
                          onClick={() => handleAction(report.id, 'actioned', 'remove_from_wall')}
                          disabled={actioningId === report.id}
                        >
                          Remove from Wall
                        </button>
                        <select
                          className="action-status-select"
                          defaultValue=""
                          disabled={actioningId === report.id}
                          onChange={(e) => handleSetStatus(report, e.target.value, e.target)}
                        >
                          <option value="" disabled>Set status…</option>
                          <option value="active">Active</option>
                          <option value="missing">Missing</option>
                          <option value="retired">Retired</option>
                          <option value="kept in kennels">Kept in Kennels</option>
                          <option value="used for breeding">Used for Breeding</option>
                          <option value="given up for adoption">Given Up for Adoption</option>
                          <option value="inactive">Inactive</option>
                          <option value="archived">Archived</option>
                          <option value="deceased">Deceased</option>
                          <option value="killed">Killed</option>
                          <option value="fallen">Fallen</option>
                          <option value="ADOPTED">Adopted</option>
                          <option value="On Rest / Season">On Rest / Season</option>
                          <option value="Absent Due to Injury">Absent Due to Injury</option>
                        </select>
                        <button
                          className="action-btn action-dismiss"
                          onClick={() => handleAction(report.id, 'dismissed', null)}
                          disabled={actioningId === report.id}
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                    {report.status !== 'pending' && (
                      <span className="actioned-label">{report.status}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selectedDogName && (
        <DogEditModal
          dogName={selectedDogName}
          currentUser={currentUser}
          onClose={() => setSelectedDogName(null)}
          onSaved={() => { fetchReports(); }}
        />
      )}
    </div>
  );
}

export default FoundReports;
